import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Search, Plus, RefreshCw, ChevronDown, MoreVertical,
  Eye, Edit2, Trash2, Send, CheckCircle, PackageCheck,
  XCircle, X, Loader2, ClipboardList, Building2,
  Calendar, Filter, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import branchService from '../../../../services/branchService';
import { useAuth } from '../../../../context/AuthContext';
import PurchaseOrderForm from './PurchaseOrderForm';
import PurchaseOrderDetail from './PurchaseOrderDetail';
import ReceivePOModal from './ReceivePOModal';
import { P } from '@/config/permissionCodes';

const STATUS_OPTIONS = [
  { value: 'all',                label: 'All Statuses' },
  { value: 'draft',              label: 'Draft' },
  { value: 'pending',            label: 'Pending' },
  { value: 'approved',           label: 'Approved' },
  { value: 'received',           label: 'Received' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'cancelled',          label: 'Cancelled' },
];

const STATUS_BADGE = {
  draft:              'bg-slate-100 text-slate-600',
  pending:            'bg-amber-100 text-amber-700',
  approved:           'bg-blue-100 text-blue-700',
  received:           'bg-green-100 text-green-700',
  partially_received: 'bg-violet-100 text-violet-700',
  cancelled:          'bg-red-100 text-red-700',
};

const STATUS_LABEL = {
  draft:              'Draft',
  pending:            'Pending',
  approved:           'Approved',
  received:           'Received',
  partially_received: 'Partially Received',
  cancelled:          'Cancelled',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const normalisePOList = (response) => {
  if (!response) return { orders: [], stats: null, total: 0 };
  if (Array.isArray(response))
    return { orders: response, stats: null, total: response.length };
  const orders = Array.isArray(response.data)   ? response.data    :
                 Array.isArray(response.content) ? response.content : [];
  const stats  = response.stats ?? null;
  const total  = response.totalElements ?? response.total ?? orders.length;
  return { orders, stats, total };
};

const normaliseSuppliers = (response) => {
  if (Array.isArray(response))          return response;
  if (Array.isArray(response?.data))    return response.data;
  if (Array.isArray(response?.content)) return response.content;
  return [];
};

const normaliseStats = (response) => {
  if (!response) return null;
  const s = response.data ?? response;
  return {
    totalOrders:   s.totalOrders   ?? s.total       ?? 0,
    pendingCount:  s.pendingCount  ?? s.pending      ?? 0,
    approvedCount: s.approvedCount ?? s.approved     ?? 0,
    draftCount:    s.draftCount    ?? s.draft        ?? 0,
    totalValue:    s.totalValue    ?? s.totalAmount  ?? 0,
    avgOrderValue: s.avgOrderValue ?? s.averageValue ?? 0,
  };
};

// ── ConfirmDialog (local, fully controlled) ───────────────────────────────────
// UI matches the original shared dialog design: rose header band with alert
// icon, message body, and footer with outlined Cancel / red Confirm buttons.
// FIX: The shared ConfirmDialog in Menu_Management/UIComponents did not invoke
//      the onCancel callback from its Cancel button, so the parent's isOpen
//      state was never set to false and the dialog stayed open. This local
//      version is fully controlled by props:
//        • Renders nothing (unmounts) when isOpen is false.
//        • Cancel button, backdrop click, and Esc key ALL call onCancel.
//        • No internal open/close state that could fall out of sync.
//      Defined here so the shared component (and Menu Management screens that
//      depend on its current behaviour) are left untouched.
function ConfirmDialog({ isOpen, title, message, onConfirm, onCancel }) {
  // Esc key closes the dialog
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null; // fully unmounts when closed

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onCancel} // backdrop click closes
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()} // clicks inside must not close
      >
        {/* Header — rose band with alert icon */}
        <div className="flex items-center gap-3 bg-red-50 px-6 py-5">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-red-100 shrink-0">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </span>
          <h3 id="confirm-dialog-title" className="text-lg font-semibold text-slate-900">
            {title}
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-base text-slate-700">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-5">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Access Denied ─────────────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view Purchase Orders.</p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PurchaseOrderList() {
  const { user, hasPermission } = useAuth();
  const isOwner = user?.userType === 'SUPER_USER';

  // ── Permissions ───────────────────────────────────────────────────────────────
  const canViewScreen = hasPermission(P.INVENTORY.PURCHASE_ORDERS.SCREEN);
  const canCreate     = hasPermission(P.INVENTORY.PURCHASE_ORDERS.CREATE);
  const canEdit       = hasPermission(P.INVENTORY.PURCHASE_ORDERS.EDIT);
  const canDelete     = hasPermission(P.INVENTORY.PURCHASE_ORDERS.DELETE);

  if (!canViewScreen) return <AccessDenied />;

  // ── Data state ────────────────────────────────────────────────────────────────
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stats, setStats]                   = useState(null);
  const [suppliers, setSuppliers]           = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [totalCount, setTotalCount]         = useState(0);

  // ── Branch ────────────────────────────────────────────────────────────────────
  const [branches, setBranches]                     = useState([]);
  const [selectedBranchId, setSelectedBranchId]     = useState(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const branchRef = useRef(null);

  // ── Filters ───────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]       = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [fromDate, setFromDate]             = useState('');
  const [toDate, setToDate]                 = useState('');

  // ── Dropdowns ─────────────────────────────────────────────────────────────────
  const [showStatusDropdown, setShowStatusDropdown]     = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [openMenuId, setOpenMenuId]                     = useState(null);
  const statusRef   = useRef(null);
  const supplierRef = useRef(null);
  // NOTE: No menuRef — stopPropagation on the toggle button + panel handles
  //       click-outside correctly without the race condition a shared ref caused.

  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });


  // ── Modals ────────────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen]       = useState(false);
  const [detailOpen, setDetailOpen]   = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [selectedPO, setSelectedPO]   = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null,
  });

  const closeConfirmDialog = () =>
    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });

  // ── Fetch purchase orders ─────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (selectedBranchId)         filters.branchId   = selectedBranchId;
      if (statusFilter !== 'all')   filters.status     = statusFilter;
      if (supplierFilter !== 'all') filters.supplierId = supplierFilter;
      if (searchQuery)              filters.search     = searchQuery;
      if (fromDate)                 filters.fromDate   = fromDate;
      if (toDate)                   filters.toDate     = toDate;

      const response = await purchaseOrderService.getAll(filters);
      const { orders, stats: embeddedStats, total } = normalisePOList(response);

      // FIX: Normalise status to lowercase once so all comparisons use 'draft',
      //      'pending', etc. — avoids 'DRAFT' vs 'draft' mismatches downstream.
      const normalisedOrders = orders.map((po) => ({
        ...po,
        status: po.status?.toLowerCase() ?? '',
      }));

      setPurchaseOrders(normalisedOrders);
      setTotalCount(total);

      if (embeddedStats) {
        setStats(embeddedStats);
      } else {
        purchaseOrderService
          .getStats(selectedBranchId)
          .then((s) => setStats(normaliseStats(s)))
          .catch(() => {});
      }
    } catch (err) {
      toast.error('Failed to load purchase orders');
      setPurchaseOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBranchId, statusFilter, supplierFilter, searchQuery, fromDate, toDate]);

  // ── Fetch suppliers ───────────────────────────────────────────────────────────
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await purchaseOrderService.getSuppliers();
      setSuppliers(normaliseSuppliers(response));
    } catch (err) {
      console.warn('Could not load suppliers:', err?.response?.status, err?.message);
      setSuppliers([]);
    }
  }, []);

  // ── Fetch branches (SUPER_USER only) ─────────────────────────────────────────
  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches()
      .then((result) => { if (result?.length > 1) setBranches(result); })
      .catch(console.error);
  }, [isOwner]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);
  useEffect(() => { fetchData();      }, [fetchData]);

  // ── Click-outside handler ─────────────────────────────────────────────────────
  // Closes all dropdowns on any outside click. The action menu toggle button and
  // its panel use stopPropagation so they don't trigger this accidentally.
  useEffect(() => {
    const handler = (e) => {
      if (statusRef.current   && !statusRef.current.contains(e.target))   setShowStatusDropdown(false);
      if (supplierRef.current && !supplierRef.current.contains(e.target)) setShowSupplierDropdown(false);
      if (branchRef.current   && !branchRef.current.contains(e.target))   setShowBranchDropdown(false);
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Client-side search filter ─────────────────────────────────────────────────
  const filteredPOs = useMemo(() => {
    if (!searchQuery) return purchaseOrders;
    const q = searchQuery.toLowerCase();
    return purchaseOrders.filter(
      (po) =>
        po.poNumber?.toLowerCase().includes(q) ||
        po.supplierName?.toLowerCase().includes(q),
    );
  }, [purchaseOrders, searchQuery]);

  // ── Pending count (FIX) ───────────────────────────────────────────────────────
  // The stats endpoint can report pendingCount = 0 even when pending POs exist
  // (server-side status case mismatch: it counts 'pending' while rows store
  // 'PENDING'). The fetched orders are the source of truth — their statuses are
  // already normalised to lowercase in fetchData — so derive the count from
  // them. The server value is used only when it is a positive number (i.e. the
  // endpoint is actually working), which also keeps the count correct when the
  // list is narrowed by filters that exclude pending orders.
  const derivedPendingCount = useMemo(
    () => purchaseOrders.filter((po) => po.status === 'pending').length,
    [purchaseOrders],
  );
  const pendingCountDisplay =
    Number(stats?.pendingCount) > 0 ? stats.pendingCount : derivedPendingCount;

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleCreate = () => { if (!canCreate) return; setSelectedPO(null); setFormOpen(true); };

  const handleView = async (po) => {
    try {
      const response = await purchaseOrderService.getById(po.id);
      const detail = response?.data ?? response;
      setSelectedPO({
        ...detail,
        status: detail.status?.toLowerCase() ?? '',
      });
      // FIX: Only open the detail modal after a successful fetch — previously
      //      it opened even on failure, showing a stale or empty PO.
      setDetailOpen(true);
    } catch (err) {
      toast.error('Failed to load purchase order details');
    }
  };

  const handleEdit   = (po) => { if (!canEdit) return; setSelectedPO(po); setFormOpen(true); };

  const handleSubmit = async (po) => {
    try {
      await purchaseOrderService.updateStatus(po.id, 'pending');
      toast.success('PO submitted for approval');
      fetchData();
    } catch { toast.error('Failed to submit PO'); }
    setOpenMenuId(null);
  };

  const handleApprove = async (po) => {
    try {
      await purchaseOrderService.updateStatus(po.id, 'approved');
      toast.success('PO approved');
      fetchData();
    } catch { toast.error('Failed to approve PO'); }
    setOpenMenuId(null);
  };

  const handleReceive = async (po) => {
    setOpenMenuId(null);
    try {
      const response = await purchaseOrderService.getById(po.id);
      const detail = response?.data ?? response;
      setSelectedPO({
        ...detail,
        status: detail.status?.toLowerCase() ?? '',
      });
      // FIX: Only open the receive modal after a successful fetch.
      setReceiveOpen(true);
    } catch (err) {
      toast.error('Failed to load purchase order details');
    }
  };

  const handleCancel = (po) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Purchase Order',
      message: `Cancel ${po.poNumber}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await purchaseOrderService.updateStatus(po.id, 'cancelled');
          toast.success('PO cancelled');
          fetchData();
        } catch { toast.error('Failed to cancel PO'); }
        closeConfirmDialog();
      },
    });
    setOpenMenuId(null);
  };

  const handleDelete = (po) => {
    if (!canDelete) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Purchase Order',
      message: `Delete ${po.poNumber}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await purchaseOrderService.delete(po.id);
          toast.success('PO deleted');
          fetchData();
        } catch { toast.error('Failed to delete PO'); }
        closeConfirmDialog();
      },
    });
    setOpenMenuId(null);
  };

  const handleFormSave    = () => { setFormOpen(false);    setSelectedPO(null); fetchData(); };
  const handleReceiveSave = () => { setReceiveOpen(false); setSelectedPO(null); fetchData(); };

  const clearFilters = () => {
    setSearchQuery(''); setStatusFilter('all');
    setSupplierFilter('all'); setFromDate(''); setToDate('');
  };

  const hasActiveFilters =
    searchQuery || statusFilter !== 'all' || supplierFilter !== 'all' || fromDate || toDate;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 max-w-full">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-teal-600" />
            Purchase Orders
          </h1>
          {stats && (
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
              <span>{stats.totalOrders ?? totalCount} Total</span>
              <span className="text-amber-600">{pendingCountDisplay} Pending</span>
              <span className="font-medium text-slate-700">
                Value: ₹{(stats.totalValue ?? 0).toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Branch Dropdown (SUPER_USER only) */}
          {isOwner && branches.length > 1 && (
            <div className="relative" ref={branchRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowBranchDropdown(!showBranchDropdown); }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                {selectedBranchId
                  ? branches.find((b) => b.id === selectedBranchId)?.name || 'Branch'
                  : 'All Branches'}
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>
              {showBranchDropdown && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={() => { setSelectedBranchId(null); setShowBranchDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${!selectedBranchId ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                  >
                    All Branches
                  </button>
                  {branches.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => { setSelectedBranchId(b.id); setShowBranchDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${selectedBranchId === b.id ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {canCreate && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Create PO
            </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search PO # or supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
          />
        </div>

        <div className="relative" ref={statusRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            {STATUS_OPTIONS.find((s) => s.value === statusFilter)?.label || 'Status'}
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
          {showStatusDropdown && (
            <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${statusFilter === opt.value ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative" ref={supplierRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowSupplierDropdown(!showSupplierDropdown); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {supplierFilter === 'all'
              ? 'All Suppliers'
              : suppliers.find((s) => String(s.id) === supplierFilter)?.name || 'Supplier'}
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
          {showSupplierDropdown && (
            <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1 max-h-48 overflow-y-auto">
              <button
                onClick={() => { setSupplierFilter('all'); setShowSupplierDropdown(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${supplierFilter === 'all' ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
              >
                All Suppliers
              </button>
              {suppliers.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSupplierFilter(String(s.id)); setShowSupplierDropdown(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${supplierFilter === String(s.id) ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <div className="relative">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="pl-7 pr-2 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 w-[130px]"
              title="From date"
            />
          </div>
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-2 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 w-[130px]"
            title="To date"
          />
        </div>

        <button
          onClick={fetchData}
          className="p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
            <span className="ml-2 text-sm text-slate-500">Loading purchase orders...</span>
          </div>
        ) : filteredPOs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mb-3" />
            <h3 className="text-sm font-semibold text-slate-600 mb-1">
              {hasActiveFilters ? 'No matching purchase orders' : 'No purchase orders yet'}
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first purchase order to get started'}
            </p>
            {!hasActiveFilters && canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Purchase Order
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left   px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">PO #</th>
                  <th className="text-left   px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Supplier</th>
                  <th className="text-left   px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Order Date</th>
                  <th className="text-left   px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Expected Delivery</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Items</th>
                  <th className="text-right  px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Total (₹)</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPOs.map((po) => (
                  <tr key={po.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{po.poNumber}</td>
                    <td className="px-4 py-3 text-slate-600">{po.supplierName}</td>
                    <td className="px-4 py-3 text-slate-500">{po.orderDate}</td>
                    <td className="px-4 py-3 text-slate-500">{po.expectedDeliveryDate || '—'}</td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {po.lineItems?.length ?? po.itemCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-800">
                      ₹{(po.totalAmount ?? po.grandTotal ?? 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[po.status] || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_LABEL[po.status] || po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openMenuId === po.id) {
                              setOpenMenuId(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPosition({
                                top:   rect.bottom + window.scrollY + 4,
                                right: window.innerWidth - rect.right,
                              });
                              setOpenMenuId(po.id);
                            }
                          }}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && filteredPOs.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-500">
            Showing {filteredPOs.length} of {totalCount} purchase orders
          </div>
        )}
      </div>

      {/* Action Menu Portal — rendered once, outside the table, to avoid overflow clipping */}
      {openMenuId && (() => {
        const po = filteredPOs.find((p) => p.id === openMenuId);
        if (!po) return null;
        return (
          <div
            style={{ position: 'fixed', top: menuPosition.top, right: menuPosition.right, zIndex: 50 }}
            className="w-44 bg-white border border-slate-200 rounded-lg shadow py-1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { handleView(po); setOpenMenuId(null); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
            >
              <Eye className="w-3.5 h-3.5" /> View Details
            </button>

            {canEdit && po.status === 'draft' && (
              <button
                onClick={() => { handleEdit(po); setOpenMenuId(null); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            )}

            {canEdit && po.status === 'draft' && (
              <button
                onClick={() => handleSubmit(po)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-blue-700 hover:bg-blue-50"
              >
                <Send className="w-3.5 h-3.5" /> Submit for Approval
              </button>
            )}

            {canEdit && po.status === 'pending' && (
              <button
                onClick={() => handleApprove(po)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-green-700 hover:bg-green-50"
              >
                <CheckCircle className="w-3.5 h-3.5" /> Approve
              </button>
            )}

            {canEdit && (po.status === 'approved' || po.status === 'partially_received') && (
              <button
                onClick={() => handleReceive(po)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-violet-700 hover:bg-violet-50"
              >
                <PackageCheck className="w-3.5 h-3.5" /> Receive Items
              </button>
            )}

            {canEdit && !['cancelled', 'received'].includes(po.status) && (
              <button
                onClick={() => handleCancel(po)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5" /> Cancel
              </button>
            )}

            {canDelete && po.status === 'draft' && (
              <>
                <div className="border-t border-slate-100 my-1" />
                <button
                  onClick={() => handleDelete(po)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* Modals */}
      {formOpen && (canCreate || canEdit) && (
        <PurchaseOrderForm
          po={selectedPO}
          suppliers={suppliers}
          onClose={() => { setFormOpen(false); setSelectedPO(null); }}
          onSave={handleFormSave}
        />
      )}

      {detailOpen && selectedPO && (
        <PurchaseOrderDetail
          po={selectedPO}
          canEdit={canEdit}
          canDelete={canDelete}
          onClose={() => { setDetailOpen(false); setSelectedPO(null); }}
          onEdit={() => { setDetailOpen(false); handleEdit(selectedPO); }}
          onSubmit={() => { handleSubmit(selectedPO); setDetailOpen(false); setSelectedPO(null); }}
          onApprove={() => { handleApprove(selectedPO); setDetailOpen(false); setSelectedPO(null); }}
          onReceive={() => { setDetailOpen(false); handleReceive(selectedPO); }}
          onCancel={() => { setDetailOpen(false); handleCancel(selectedPO); }}
        />
      )}

      {receiveOpen && selectedPO && canEdit && (
        <ReceivePOModal
          po={selectedPO}
          onClose={() => { setReceiveOpen(false); setSelectedPO(null); }}
          onSave={handleReceiveSave}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
}